/**
 * People & Org Planning — Org Planning Overview (formerly "Internal Playground · Hiring Capacity
 * Timeline", then briefly "Testing Ground"; the route keeps its old slugs so existing links still
 * resolve).
 *
 * Row hierarchy (rebuilt Sep 2026): a genuine recursive org chart rooted at the CEO ("FINN"),
 * following real reporting lines all the way down — not a fixed Department → Team → Manager →
 * Role tree. See `proxy-server/lib/internalPlaygroundTimeline.js`'s module doc for the full
 * reasoning, including the explicit, named exceptions to Personio's literal reporting lines and
 * why a `Hired` role never contributes its own FTE.
 *
 * Split into tabs (Sep 2026): the Overview tab is the department-level summary — FINN plus one
 * row per department, capped so a department row can't expand further — and each department gets
 * its own URL-routed tab showing the exact same recursive/expandable table the single view used to
 * be, just re-rooted at that department instead of FINN. There is exactly one query behind every
 * tab (`fetchHiringCapacityTimeline` fetches the whole tree at once), so tabs are a pure
 * client-side reslicing — no new proxy work, no per-tab fetch. See `OrgChartTable`'s `maxDepth`
 * prop for how the two modes share one renderer, and `departmentTabs.ts` for how a department node
 * becomes a URL slug.
 *
 * Access is gated at the route level (`PeopleSpace.tsx#InternalPlaygroundRoutes`), so this view
 * assumes it is allowed to be here — the proxy still re-authorizes on every request regardless.
 */
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FlaskConical, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { CockpitTabs } from './taCockpit/cockpitTabs';
import { BLUE_BUTTON, BUTTON_CONTENT, RADIUS } from './orgPlanningChrome';
import { DataQualityBanner } from './internalPlayground/DataQualityBanner';
import { buildMonthGroups, OrgChartTable } from './internalPlayground/OrgChartTable';
import { buildDepartmentTabs, slugifyDepartment } from './internalPlayground/departmentTabs';
import { TimelineFilterBar } from './internalPlayground/TimelineFilterBar';
import { ProposeRoleModal } from './internalPlayground/ProposeRoleModal';
import { EditRoleModal, type EditRoleTarget } from './internalPlayground/EditRoleModal';
import { ViewerScopeContext, viewerScopeFrom } from './internalPlayground/viewerScope';
import { type BackfillPerson, ManageBackfillDialog } from './internalPlayground/BackfillControl';
import type { BackfillTarget } from './internalPlayground/backfillForm';
import {
  defaultTimelineFilters,
  filterOrgTree,
  fteByMonthByNodeId,
  isFilteringActive,
  rolesInScope,
  todayIso,
} from './internalPlayground/timelineFilters';
import {
  fetchHiringCapacityTimeline,
  InternalPlaygroundAccessError,
  notifyOutcome,
  type OrgNode,
} from '../services/internalPlaygroundApi';

const BASE_PATH = '/internal-playground/hiring-capacity-timeline';
const OVERVIEW_TAB = 'overview';

function InternalPlaygroundHiringTimelineContent() {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['internal-playground-hiring-timeline'],
    queryFn: fetchHiringCapacityTimeline,
    retry: (count, err) => !(err instanceof InternalPlaygroundAccessError) && count < 2,
    // This pipeline runs two Airtable scans plus a 24-month BigQuery query — the underlying
    // hiring plan/headcount data doesn't change minute to minute, so a remount (e.g. tabbing away
    // and back) shouldn't re-run all of that. The Refresh button still forces it via `refetch`.
    staleTime: 5 * 60 * 1000,
  });
  // One context value per fetched `viewer`, not per render: a fresh object would re-render every
  // row's Edit/backfill control and defeat the hiring-manager memos in both modals. Above the early
  // returns below, as every hook must be.
  const viewerScope = useMemo(() => viewerScopeFrom(data?.viewer), [data?.viewer]);
  // Ids the person has manually expanded/collapsed away from their level's default (see
  // `defaultExpanded` in `OrgChartTable`) — never which ids ARE expanded, so a node that appears
  // later (a refetch adding a department) still gets the right default instead of inheriting a
  // stale membership decision made before it existed. Deliberately shared across every tab: node
  // ids are globally unique, and switching departments shouldn't reset what you had open.
  const [toggledIds, setToggledIds] = useState<Set<string>>(new Set());

  const groups = useMemo(() => (data ? buildMonthGroups(data.months) : []), [data]);
  // Trimester GROUPS the person has manually expanded, Google-Sheets-column-grouping style —
  // every group defaults to collapsed (its last month only), so this only needs to track the
  // exceptions, the same reasoning as `toggledIds` above. Also shared across tabs on purpose:
  // it's a viewing preference (which trimesters are broken into months), not a per-department one.
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set());

  // All three filters are viewing preferences shared across every tab, like the toggle state
  // above (David, Sep 2026: switching departments should not silently drop a filter you set).
  // A Role carried over from another department's tab that this tab doesn't have simply matches
  // nothing here — the honest, predictable consequence of persisting the selection, not a bug —
  // and switching back re-applies it exactly as left.
  const [filters, setFilters] = useState(defaultTimelineFilters());
  const [proposing, setProposing] = useState(false);
  /** The leaver whose Manage backfill dialog is open, and — once "I want to backfill" is chosen —
   * the one the propose form is pre-filled for. Never both at once. */
  const [backfillPerson, setBackfillPerson] = useState<BackfillPerson | null>(null);
  const [backfillTarget, setBackfillTarget] = useState<BackfillTarget | null>(null);
  /**
   * Set when a proposal was filed but the approvers were NOT told, and only when that is a real
   * failure rather than an environment where the announcement is deliberately off — see
   * `notifyOutcome`. It lives here rather than in the modal because the modal closes on success,
   * and a warning that leaves with it is one nobody reads.
   */
  const [notifyFailed, setNotifyFailed] = useState(false);
  /** Filed, but the suggested priority could not be saved. Same reasoning as `notifyFailed`: the
   * modal has closed, so the page has to say it. */
  const [priorityDropped, setPriorityDropped] = useState(false);
  /** The role whose Edit dialog is open. */
  const [editTarget, setEditTarget] = useState<EditRoleTarget | null>(null);
  /** An edit sent a proposal back for approval but the Slack notice failed — same reasoning as
   * `notifyFailed`: the modal has closed, so the page has to say it. */
  const [reapprovalNotifyFailed, setReapprovalNotifyFailed] = useState(false);

  const columns = useMemo(
    () => groups.flatMap((g) => (expandedGroupKeys.has(g.key) ? g.months : [g.key])),
    [groups, expandedGroupKeys]
  );

  const toggle = (id: string) => {
    setToggledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (key: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" aria-label="Loading org planning overview" />
      </div>
    );
  }

  if (error instanceof InternalPlaygroundAccessError) {
    return (
      <div className="mx-auto mt-6 max-w-md rounded-md border border-zinc-200 bg-white p-4 text-sm text-zinc-600">
        You do not have access to this page.
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto mt-6 flex max-w-md items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
        <div className="text-sm text-red-800">
          <p className="font-medium">Couldn&apos;t load the org planning overview</p>
          <p className="mt-0.5 text-red-700">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  // Tabs are computed from the fetched tree (departments aren't a static list), so validation only
  // happens once data has loaded — a bare path or a stale/unknown department slug both canonicalize
  // to Overview, same reasoning as TACockpitView's `Navigate replace`.
  const tabs = [{ value: OVERVIEW_TAB, label: 'Overview', node: data.root }, ...buildDepartmentTabs(data.root)];
  const activeTab = tabs.find((t) => t.value === tabParam);
  if (!activeTab) {
    return <Navigate to={`${BASE_PATH}/${OVERVIEW_TAB}`} replace />;
  }

  const goToTab = (value: string) => navigate(`${BASE_PATH}/${value}`);
  const goToDepartment = (node: OrgNode) => goToTab(slugifyDepartment(node.groupLabel ?? node.personName));

  // Role options are always drawn from the UNFILTERED tab subtree — narrowing Employment Type or
  // Status must never make a role disappear from its own picker (see `rolesInScope`'s doc).
  // Filtering itself, by contrast, always runs against the full tree so a manager row can still
  // show the right total for their filtered team (see `filterOrgTree`'s doc).
  const roleOptions = rolesInScope(activeTab.node);
  const filteredRoot = filterOrgTree(activeTab.node, filters, todayIso());
  // Only built while a filter is actually narrowing the tree — its mere presence is what
  // `OrgChartTable` reads to decide whether to show the greyed-out "unfiltered" figure at all.
  const unfilteredFteById = isFilteringActive(filters) ? fteByMonthByNodeId(activeTab.node) : undefined;

  return (
    // A proposer is offered "Edit", "Manage backfill" and hiring managers only in their own line.
    <ViewerScopeContext.Provider value={viewerScope}>
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CockpitTabs
          value={activeTab.value}
          tabs={tabs}
          onChange={goToTab}
          shape="soft"
          ariaLabel="Org planning overview sections"
        />
        <div className="flex items-center gap-2">
          <TimelineFilterBar filters={filters} onChange={setFilters} roleOptions={roleOptions} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={BLUE_BUTTON}
            contentClassName={BUTTON_CONTENT}
            onClick={() => setProposing(true)}
          >
            <Plus className="h-4 w-4" />
            Propose a role
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={BLUE_BUTTON}
            contentClassName={BUTTON_CONTENT}
            onClick={() => void refetch()}
            loading={isFetching}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {filteredRoot ? (
        <OrgChartTable
          root={filteredRoot}
          columns={columns}
          groups={groups}
          expandedGroupKeys={expandedGroupKeys}
          onToggleGroup={toggleGroup}
          toggledIds={toggledIds}
          onToggle={toggle}
          maxDepth={activeTab.value === OVERVIEW_TAB ? 1 : Infinity}
          onDeadEndClick={activeTab.value === OVERVIEW_TAB ? goToDepartment : undefined}
          flat={activeTab.value === OVERVIEW_TAB}
          unfilteredFteById={unfilteredFteById}
          onBackfillOpen={setBackfillPerson}
          edit={{ onEdit: setEditTarget }}
        />
      ) : (
        <div className="rounded-md border border-zinc-200 bg-white p-6 text-center text-sm text-zinc-500">
          Nothing matches these filters in {activeTab.label}.
        </div>
      )}

      {/* Always the unfiltered `data.root`, never `filteredRoot`: the manager picker is derived
          from the tree, and a Status/Role filter narrowing the page must not narrow who a role can
          be proposed to. */}
      {proposing && (
        <ProposeRoleModal
          root={data.root}
          onClose={() => setProposing(false)}
          onProposed={(notify, dropped) => {
            setNotifyFailed(notify === 'failed');
            setPriorityDropped(Boolean(dropped));
          }}
        />
      )}

      {backfillPerson && (
        <ManageBackfillDialog
          person={backfillPerson}
          onClose={() => setBackfillPerson(null)}
          onBackfill={(person) => {
            setBackfillPerson(null);
            if (person.backfill.state === 'open') {
              setBackfillTarget({
                employeeId: person.employeeId,
                name: person.name,
                terminationDate: person.terminationDate,
                draft: person.backfill.draft,
              });
            }
          }}
        />
      )}

      {/* Same unfiltered tree as above, for the same reason. */}
      {backfillTarget && (
        <ProposeRoleModal
          root={data.root}
          backfill={backfillTarget}
          onClose={() => setBackfillTarget(null)}
          onProposed={(notify, dropped) => {
            setNotifyFailed(notify === 'failed');
            setPriorityDropped(Boolean(dropped));
          }}
        />
      )}

      {/* Unfiltered tree for the manager picker, as for proposing. */}
      {editTarget && (
        <EditRoleModal
          target={editTarget}
          root={data.root}
          onClose={() => setEditTarget(null)}
          onSaved={(result) =>
            setReapprovalNotifyFailed(
              (result.reapproval || result.changeRequested) &&
                notifyOutcome({ notified: result.notified, notifyReason: result.notifyReason ?? undefined }) === 'failed'
            )
          }
        />
      )}

      {reapprovalNotifyFailed && (
        <div
          role="alert"
          className={`${RADIUS} flex items-start gap-3 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900`}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <p>
              The change is waiting for approval on the Role Approvals page, but the approvers could not
              be told in Slack. Let them know it needs their decision.
            </p>
            <button
              type="button"
              className="mt-1 text-xs underline underline-offset-2"
              onClick={() => setReapprovalNotifyFailed(false)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {notifyFailed && (
        <div
          role="alert"
          className={`${RADIUS} flex items-start gap-3 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900`}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <p>
              The role was filed, but the approvers could not be notified in Slack. Nothing is lost
              — it is in the queue on the Role Approvals page — but nobody has been told about it.
            </p>
            <button
              type="button"
              className="mt-1 text-xs underline underline-offset-2"
              onClick={() => setNotifyFailed(false)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {priorityDropped && (
        <div
          role="alert"
          className={`${RADIUS} flex items-start gap-3 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900`}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <p>
              The role was filed, but its suggested priority could not be saved. Mention it in the
              Slack request — the CEO sets the approved priority on Role Approvals either way.
            </p>
            <button
              type="button"
              className="mt-1 text-xs underline underline-offset-2"
              onClick={() => setPriorityDropped(false)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-zinc-400">
        FTE = weekly working hours ÷ 40 for hired people; open/backlog/on-hold roles default to 1.0
        (0.5 for Working Students) from their planned/adjusted/actual start month onward. The org
        chart reflects who&apos;s <em>currently</em> employed — someone who has since left FINN no
        longer appears, including in past months they were actually part of. Generated{' '}
        {new Date(data.generatedAt).toLocaleString()}.
      </p>

      {/* Bottom of the page on purpose (David, Sep 2026) — nothing it names contributes FTE to the
          table, so it reads as a footnote about the figures rather than a gate in front of them. */}
      <DataQualityBanner dataQuality={data.dataQuality} />
    </div>
    </ViewerScopeContext.Provider>
  );
}

export default function InternalPlaygroundHiringTimelineView() {
  return (
    <div className="mx-auto max-w-[1400px] px-4 py-8">
      <header className="mb-6 flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900">
          <FlaskConical className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Org Planning Overview</h1>
          <p className="text-sm text-zinc-500">Known Team (Personio) + Planned Roles (Hiring DB); 2026 - 2027</p>
        </div>
      </header>
      <InternalPlaygroundHiringTimelineContent />
    </div>
  );
}
